import React, { useState, useMemo } from 'react';
import { Table, Input, Modal, Select, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import Search from 'antd/lib/transfer/search';
import Button from '@mui/material/Button';
import { message } from 'antd';

const EmailTable = ({
    intl,
    data,
    selectedKeys = [],
    onSelectionChange,
    placeholderId,
    titleKey,
    searchKey = null,
    columns = null,
    rowSelectionType = 'checkbox',
    scroll = { y: 240 },
    disabled = false,
    className = '',
    branchid,
    thunksDeleteEmails,
    thunksPutEmails,
    thunksPostEmails,
    getEmailsByBranchId,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [editingKey, setEditingKey] = useState('');
    const [editedValue, setEditedValue] = useState('');
    const [editedLanguage, setEditedLanguage] = useState('fr');

    const refreshEmailList = async () => {
        await getEmailsByBranchId(branchid);
    };

    const handleDelete = async (record) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'automaticReport.confirmDelete' }),
            content: intl.formatMessage(
                { id: 'automaticReport.confirmDeleteEmail' },
                { email: record[titleKey] }
            ),
            okText: intl.formatMessage({ id: 'automaticReport.yes' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'automaticReport.no' }),
            onOk: async () => {
                try {
                    await thunksDeleteEmails(branchid, Number(record.key));
                    await refreshEmailList();
                    message.success(
                        intl.formatMessage({ id: 'automaticReport.form.emaildeleted' })
                    );
                } catch (error) {
                    console.error('Failed to delete email:', error);
                }
            },
        });
    };

    const startEditing = (record) => {
        setEditingKey(record.key);
        setEditedValue(record[titleKey]);
        setEditedLanguage(record.languageCode || 'fr');
    };

    const cancelEditing = () => {
        setEditingKey('');
        setEditedValue('');
        setEditedLanguage('fr');
    };

    const saveEditing = async (record) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(editedValue)) {
            return alert(
                intl.formatMessage({
                    id: 'automaticReport.pleaseenteravalidemailaddress',
                })
            );
        }

        try {
            const body = {
                emailAddress: editedValue,
                countryCode: 124,
                languageCode: editedLanguage,
            };
            const emailid = Number(record.key);
            await thunksPutEmails(branchid, emailid, body);
            setEditingKey('');
            setEditedValue('');
            setEditedLanguage('fr');
            await refreshEmailList();
            message.success(intl.formatMessage({ id: 'automaticReport.form.emailedited' }));
        } catch (error) {
            console.error('Failed to update email:', error);
        }
    };

    const handleAdd = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(searchTerm)) {
            return alert(
                intl.formatMessage({
                    id: 'automaticReport.pleaseenteravalidemailaddress',
                })
            );
        }

        const body = {
            emailAddress: searchTerm,
            countryCode: 124,
            languageCode: editedLanguage,
        };
        try {
            await thunksPostEmails(branchid, body);
            await refreshEmailList();
            message.success(intl.formatMessage({ id: 'automaticReport.form.emailcreated' }));
        } catch (error) {
            console.error('Failed to add email:', error);
        }
    };

    const filteredData = useMemo(
        () => {
            if (!data) return [];

            const searchField = searchKey || titleKey;
            return data.filter((item) => {
                if (!searchTerm) return true;

                if (searchField) {
                    const fieldValue = item[searchField];
                    return String(fieldValue)
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase());
                }

                if (columns) {
                    return columns.some((col) => {
                        if (!col.dataIndex) return false;
                        const value = item[col.dataIndex];
                        return String(value)
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase());
                    });
                }

                return true;
            });
        },
        [data, searchTerm, searchKey, titleKey, columns]
    );

    const filteredDataKeys = useMemo(() => filteredData.map((item) => item.key), [filteredData]);

    const tableColumns = columns || [
        {
            title: (
                <FormattedMessage
                    id={`automaticReport.list.table.${
                        titleKey === 'vehicleName' ? 'vehicles' : 'emails'
                    }`}
                />
            ),
            dataIndex: titleKey,
            render: (text, record) => {
                if (record.key === editingKey) {
                    return (
                        <Input
                            value={editedValue}
                            onChange={(e) => setEditedValue(e.target.value)}
                        />
                    );
                }
                return <a>{text}</a>;
            },
        },
        {
            title: intl.formatMessage({ id: 'automaticReport.reportlanguage' }),
            dataIndex: 'languageCode',
            render: (text, record) => {
                if (record.key === editingKey) {
                    return (
                        <Select
                            value={editedLanguage}
                            onChange={(value) => setEditedLanguage(value)}
                            style={{ width: 120 }}
                        >
                            <Select.Option value="en">
                                {intl.formatMessage({ id: 'automaticReport.language.english' })}
                            </Select.Option>
                            <Select.Option value="fr">
                                {intl.formatMessage({ id: 'automaticReport.language.french' })}
                            </Select.Option>
                            <Select.Option value="es">
                                {intl.formatMessage({ id: 'automaticReport.language.spanish' })}
                            </Select.Option>
                        </Select>
                    );
                }
                return (
                    <div>
                        {text === 'fr' && (
                            <a>{intl.formatMessage({ id: 'automaticReport.language.french' })}</a>
                        )}
                        {text === 'en' && (
                            <a>{intl.formatMessage({ id: 'automaticReport.language.english' })}</a>
                        )}
                        {text === 'es' && (
                            <a>{intl.formatMessage({ id: 'automaticReport.language.spanish' })}</a>
                        )}
                    </div>
                );
            },
        },
        {
            title: '',
            dataIndex: 'actions',
            align: 'right',
            render: (_, record) => {
                if (record.key === editingKey) {
                    return (
                        <div>
                            <Button
                                size="small"
                                type="primary"
                                onClick={() => saveEditing(record)}
                                disabled={disabled}
                                style={{ marginRight: 8, color: 'green' }}
                            >
                                {intl.formatMessage({
                                    id: 'automaticReport.save',
                                })}
                            </Button>
                            <Button
                                size="small"
                                onClick={cancelEditing}
                                disabled={disabled}
                                style={{ color: 'green' }}
                            >
                                {intl.formatMessage({
                                    id: 'automaticReport.cancel',
                                })}
                            </Button>
                        </div>
                    );
                }
                return (
                    <div>
                        <Button
                            onClick={() => startEditing(record)}
                            disabled={disabled || editingKey !== ''}
                            style={{ color: 'green' }}
                        >
                            <i className="far fa-edit" aria-hidden="true" />
                        </Button>
                        <Button
                            onClick={() => handleDelete(record)}
                            disabled={disabled || editingKey !== ''}
                            style={{ color: 'green' }}
                        >
                            <i className="far fa-trash-alt" aria-hidden="true" />
                        </Button>
                    </div>
                );
            },
        },
    ];

    const rowSelection =
        rowSelectionType === 'none'
            ? undefined
            : {
                  type: rowSelectionType,
                  selectedRowKeys: selectedKeys,
                  //onChange: onSelectionChange,
                  onChange: (selectedRowKeys, selectedRows) => {
                      const newSelectedKeys = [
                          ...selectedKeys.filter((key) => !filteredDataKeys.includes(key)),
                          ...selectedRowKeys,
                      ];
                      onSelectionChange(newSelectedKeys, selectedRows);
                  },
                  getCheckboxProps: () => ({ disabled: disabled || editingKey !== '' }),
              };

    return (
        <div className={`searchable-table ${className}`}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <Input
                    placeholder={intl.formatMessage({
                        id: placeholderId,
                        defaultMessage: 'Search...',
                    })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    allowClear
                    disabled={disabled}
                    style={{ flex: 1 }}
                />
                <Select
                    value={editedLanguage}
                    onChange={(value) => setEditedLanguage(value)}
                    style={{ width: 120 }}
                    disabled={disabled || editingKey !== ''}
                >
                    <Select.Option value="en">
                        {intl.formatMessage({ id: 'automaticReport.language.english' })}
                    </Select.Option>
                    <Select.Option value="fr">
                        {intl.formatMessage({ id: 'automaticReport.language.french' })}
                    </Select.Option>
                    <Select.Option value="es">
                        {intl.formatMessage({ id: 'automaticReport.language.spanish' })}
                    </Select.Option>
                </Select>
                <Button
                    type="primary"
                    onClick={handleAdd}
                    disabled={disabled || editingKey !== ''}
                    style={{ color: 'green' }}
                >
                    {intl.formatMessage({
                        id: 'automaticReport.add',
                    })}
                </Button>
            </div>

            <Table
                rowSelection={rowSelection}
                columns={tableColumns}
                dataSource={filteredData}
                pagination={false}
                scroll={scroll}
                rowKey="key"
                className="searchable-table-content"
            />
        </div>
    );
};

export default EmailTable;
